import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Lock } from "lucide-react";
import { api } from "@/lib/api";
import { Fx, CurrencyPicker, FxNote } from "@/components/Currency";
import { Btn, Card, Loading, SectionTitle } from "@/components/Ui";

const ORDER = ["founding", "pro", "enterprise"];

export default function Pricing() {
  const [plans, setPlans] = useState(null);
  const [note, setNote] = useState("");
  const [pricesVisible, setPricesVisible] = useState(false);
  const visiblePlans = useMemo(
    () => ORDER.filter((k) => plans?.[k]).map((k) => [k, plans[k]]), [plans]);

  useEffect(() => {
    api.get("/plans").then(({ data }) => {
      setPlans(data.plans);
      setNote(data.note);
      setPricesVisible(!!data.prices_visible);
    }).catch(() => setPlans({}));
  }, []);

  return (
    <div className="mx-auto max-w-[1240px] px-5 py-12" data-testid="pricing-page">
      <SectionTitle
        overline="Supplier subscriptions"
        title="Plans for manufacturers, distributors and stockists"
        sub="Subscriptions control how much of the matched RFQ flow you can access and how your products appear in the searchable product catalogue. Pro and Enterprise are paid online by card and activate immediately."
      />

      {pricesVisible && (
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <CurrencyPicker />
          <FxNote />
        </div>
      )}

      {!plans ? (
        <Loading label="Loading plans…" />
      ) : (
        <div className="mt-9 grid gap-5 lg:grid-cols-3">
          {visiblePlans.map(([key, p]) => {
            const featured = key === "pro";
            return (
              <Card
                key={key}
                data-testid={`plan-card-${key}`}
                className={featured ? "border-[#102A43] ring-1 ring-[#102A43]" : ""}
              >
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-black tracking-tight text-[#102A43]">{p.name}</h3>
                  {featured && (
                    <span className="rounded-full bg-[#D9A441] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-[#17212b]">
                      Recommended
                    </span>
                  )}
                </div>
                {pricesVisible ? (
                  <>
                    <p className="mt-4 font-display text-3xl font-black text-[#102A43]">
                      {p.price_aed ? `AED ${p.price_aed.toLocaleString()}` : "Free"}
                    </p>
                    {p.price_aed ? (
                      <Fx amount={p.price_aed} className="block text-sm font-semibold"
                        testId={`plan-fx-${key}`} />
                    ) : null}
                    <p className="text-xs text-[#667788]">{p.billing}</p>
                  </>
                ) : (
                  <div className="mt-4" data-testid={`plan-price-locked-${key}`}>
                    <p className="flex items-center gap-2 font-display text-lg font-black text-[#102A43]">
                      <Lock className="h-4 w-4 text-[#D9A441]" strokeWidth={3} />
                      Sign up to view pricing
                    </p>
                    <Link to="/register?role=supplier"
                      className="text-xs font-semibold text-[#147D78] underline">
                      Create a supplier account
                    </Link>
                  </div>
                )}
                <ul className="mt-5 space-y-2.5 border-t border-[#DFE6ED] pt-5">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-sm text-[#334e68]">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#147D78]" strokeWidth={3} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link to="/register?role=supplier" className="mt-6 block">
                  <Btn
                    data-testid={`plan-cta-${key}`}
                    variant={featured ? "primary" : "ghost"}
                    className="w-full"
                  >
                    {key === "founding" ? "Start free" : `Request ${p.name}`}
                  </Btn>
                </Link>
              </Card>
            );
          })}
        </div>
      )}

      <Card className="mt-8 bg-[#102A43]">
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#D9A441]">
          Scope of the subscription
        </p>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#bcccdc]">
          {note || "UAE/GCC representation services are contracted separately from the software subscription."}{" "}
          A subscription gives access to the ADIT Connect software platform only. It is not a
          commercial agency, distribution or representation agreement, and it does not make ADIT
          responsible for a supplier&apos;s products or offers.
        </p>
      </Card>
    </div>
  );
}
